// Simple IndexedDB-based cache for rendered PDF page images
// Keyed by a provided cacheKey (e.g., `${pdfId}:${version}`) and page index.

const DB_NAME = 'pdfFlipbook';
const DB_VERSION = 1;
const PAGES_STORE = 'pages';
const META_STORE = 'meta';

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      // No IndexedDB support
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PAGES_STORE)) {
        db.createObjectStore(PAGES_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function withStore(storeName, mode, fn) {
  return openDB().then((db) => {
    if (!db) return null;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const store = tx.objectStore(storeName);
      const result = fn(store);
      tx.oncomplete = () => resolve(result?._result ?? result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  });
}

export function getPage(cacheKey, pageIndex) {
  const key = `${cacheKey}:p:${pageIndex}`;
  return withStore(PAGES_STORE, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result ? req.result.data : null);
      req.onerror = () => reject(req.error);
    });
  });
}

export function setPage(cacheKey, pageIndex, dataUrl) {
  const key = `${cacheKey}:p:${pageIndex}`;
  return withStore(PAGES_STORE, 'readwrite', (store) => {
    store.put({ key, data: dataUrl, ts: Date.now() });
  });
}

export function getMeta(cacheKey) {
  const key = `${cacheKey}:meta`;
  return withStore(META_STORE, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result ? req.result.data : null);
      req.onerror = () => reject(req.error);
    });
  });
}

export function setMeta(cacheKey, meta) {
  const key = `${cacheKey}:meta`;
  return withStore(META_STORE, 'readwrite', (store) => {
    store.put({ key, data: meta, ts: Date.now() });
  });
}

export function clearCache(cacheKey) {
  const pagePrefix = `${cacheKey}:p:`;
  const metaKey = `${cacheKey}:meta`;
  return openDB().then((db) => {
    if (!db) return null;
    return new Promise((resolve, reject) => {
      const tx = db.transaction([PAGES_STORE, META_STORE], 'readwrite');
      const pStore = tx.objectStore(PAGES_STORE);
      const mStore = tx.objectStore(META_STORE);

      // Delete meta
      mStore.delete(metaKey);

      // Iterate over pages and delete those with prefix
      const req = pStore.openKeyCursor();
      req.onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) {
          const k = cursor.key;
          if (typeof k === 'string' && k.startsWith(pagePrefix)) {
            pStore.delete(k);
          }
          cursor.continue();
        }
      };

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  });
}